









































































import { Component, Vue } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import TokenInformation from '@/models/TokenInformation';
import { Dictionary } from 'vue-router/types/router';
import Theme from '@/models/Theme';

const AuthStore = namespace('auth');
const ApplicationStore = namespace('application');

@Component({
    components: {
        LocaleSwitcher: () => import(
            /* webpackChunkName: "LocaleSwitcher" */
            '@/components/locale/LocaleSwitcher.component.vue')
    }
})
export default class CodeView extends Vue {

    @AuthStore.Getter('token')
    private token!: string;

    @AuthStore.Mutation('setToken')
    private setToken!: (token: string | undefined) => void;

    @AuthStore.Action('getTokenInformation')
    private getTokenInformation!: (token: string) => Promise<TokenInformation>;

    @ApplicationStore.Mutation('setUsingCode')
    private setUsingCode!: (value: boolean) => void;

    @ApplicationStore.Getter('customTheme')
    private customTheme!: Theme;

    public CODE_INPUT_LENGTH: number = 10;
    public codeValue: string | null = null;

    public isLoading: boolean = false;
    public tokenInformation: TokenInformation | null = null;

    /**
     * Url Params for ident Token, theme token usw.
     */
    public queryParams!: Dictionary<string | (string | null)[]>;

    get codeInputElement(): HTMLInputElement {
        return this.$refs['code-input'] as Vue & HTMLInputElement;
    }

    get progress(): number {
        return Math.min(100, (this.codeValue?.length ?? 0) * 10);
    }

    get codeInputStyle(): { [key: string]: string } {
        if (this.$vuetify.breakpoint.width >= 530) {
            return {
                'max-width': '470px',
                'font-size': '27px' // > 27px causes an bug in safari
            };
        } else {
            return {
                'max-width': '300px',
                'font-size': '20px'
            };
        }
    }

    get progressBarHeight(): number {
        if (this.$vuetify.breakpoint.width >= 530) {
            return 7;
        } else {
            return 4;
        }
    }

    get buttonVisibility(): { [key: string]: string } {
        return { visibility: this.codeValue?.length === this.CODE_INPUT_LENGTH ? 'visible' : 'hidden' };
    }

    get logoDimension(): { height: string, width: string } {
        const dimension = { height: '180px', width: '180px' };

        if (this.$vuetify.breakpoint.mdAndUp) {
            dimension.height = '300px';
            dimension.width = '300px';
        }

        return dimension;
    }

    /**
     * Support function to retrive error state value in a convinient way
     */
    get hasErrorState(): boolean {
        return !!this.tokenInformation && !this.tokenInformation.isApplicable;
    }

    public created() {
        // get url query params
        this.queryParams = { ...this.$route.query };
        // copy url query params to get rid of code value in url (looks shitty)
        const newQueryParams = { ...this.$route.query };
        delete newQueryParams.input;
        delete newQueryParams.theme;

        // do not show code input, if token is available
        // if this is removed, code from Line 205 is necessary
        if (this.token) {
            this.$router.push({ name: 'library_view' });
        } else {
            this.$router.replace({ name: 'code_view', query: newQueryParams })
                // eslint-disable-next-line handle-callback-err
                .catch(() => {
                    /* do nothing (without this a NavigationDuplicated Error is thrown) */
                });
        }
    }

    public mounted() {
        this.codeInputElement.focus();
        if ('input' in this.queryParams) {
            this.setCodeValue(this.queryParams.input as string);
        }
    }

    public setCodeValue(value: string) {
        // max length does not work on mobile
        Vue.nextTick(() => {
            this.codeValue = (value || '').substring(0, this.CODE_INPUT_LENGTH).toUpperCase();
        });
    }

    public async onCodeSubmit() {
        // avoid double triggering
        if (this.isLoading) {
            return;
        }

        if (this.codeValue?.length === this.CODE_INPUT_LENGTH) {
            try {
                this.isLoading = true;
                this.tokenInformation = await this.getTokenInformation(this.codeValue);

                // check if token was applied already by user
                // TODO: this code snippet can be removed in future, it is not necessary anymore
                if (this.tokenInformation.isApplicable && this.tokenInformation.isUsersToken) {
                    Vue.nextTick(() => Vue.notifyInfoSimplified('CODE.TOKEN.ALREADY_IN_USE', null, 10000));
                    this.setToken(this.codeValue);
                    this.setUsingCode(true);
                    this.$router.push({ name: 'library_view' });
                    return;
                }

                if (this.tokenInformation.isApplicable) {
                    // check if input code is the same as saved code
                    this.setToken(this.codeValue);
                    // save that the user uses an code
                    this.setUsingCode(true);
                    this.$router.push({ name: 'root_view' });
                }
            } catch (e) {
                console.log(e);
                this.$notifyErrorSimplified('GENERAL.NOTIFICATIONS.SERVER_ERROR');
            } finally {
                this.isLoading = false;
            }
        }
    }

    /**
     * Get the logo source
     */
    public logoSrc(): string {
        let src;
        try {
            src = require(`@/assets/themes/${this.customTheme?.token}/logo.png`);
        } catch (e) {
            src = require('@/assets/themes/default/logo.png');
        }
        return src;
    }
}
